import { AnyAction, configureStore, Middleware } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PersistorOptions,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';
import { liveJobLogMiddleware } from 'src/ble/LiveJobLogMiddleware';
import { bundlesApi } from './api/bundles';
import { firmwareTiersApi } from './api/firmwareTiers';
import { organizationApi } from './api/organizations';
import { userApi } from './api/users';
import { logActions } from './log/actions';
import rootReducer from './rootReducer';
import { sentinelActions } from './sentinel/actions';
import { authActions } from './auth/actions';
import type { AppState } from './types';
import { PersistenceStatus } from './persist';

/** Middleware that ensures any time the logout action is dispatch, the persisted state is purged. */
const persistenceMiddleware: Middleware<{}, AppState> = api => next => (action: AnyAction) => {
  if (authActions.logOut.match(action)) persistor.purge();
  return next(action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          ...Object.values(sentinelActions).map(i => i.type),
          logActions.liveJobLogProgress.type,
          logActions.fetchLogSuccess.type,
        ],
        ignoredPaths: ['sentinel', 'log'],
      },
      immutableCheck: {
        warnAfter: 100,
      },
    })
      .concat(organizationApi.middleware)
      .concat(userApi.middleware)
      .concat(bundlesApi.middleware)
      .concat(firmwareTiersApi.middleware)
      .concat(liveJobLogMiddleware)
      .concat(persistenceMiddleware),
});

setupListeners(store.dispatch);

export const persistor = persistStore(store, {
  manualPersist: true, // Blocks starting up the persistence engine until we manually call persistor.persist().
} as unknown as PersistorOptions);

// Monkey-patch the persistence object so that we can keep track of in-flight operations.
const purge = persistor.purge.bind(persistor);
persistor.purge = () => {
  PersistenceStatus.isPurging = true;

  return purge().finally(() => {
    PersistenceStatus.isPurging = false;
  });
};

// If the app closed during an in-flight purge operation, ensure it completes now.
if (PersistenceStatus.isPurging) persistor.purge();
// Finally, start up the persistence engine.
persistor.persist();

if (module.hot) {
  module.hot.accept('./rootReducer', () => {
    const { default: nextRootReducer, rootPersistConfig } = require('./rootReducer');
    store.replaceReducer(persistReducer(rootPersistConfig, nextRootReducer));
  });
}
